/* eslint-disable unicorn/no-null */
import { useSelector, useDispatch } from "react-redux";
import { Button, Flex, Text, Link as CLink } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";

import { isAfter, isWithinInterval, parseISO } from "date-fns";

import { ButtonLink, useContainerValue, withContainerBreakpoints } from "ui-kit";

import { MOBILE_NAV_HEIGHT } from "components/layout/constants";
import { getActiveVacation } from "store/selectors/settings";
import { setReactivateModalOpen } from "store/actions/settings";
import { useQueryFeatureFlag } from "hooks/feature-flags";
import { FEATURE_FLAG_VACATION_MODE } from "constants/feature-flags";
import { getUserData } from "store/selectors/user";

import { YellowAlertInfo } from "../../ui-kit/icon";

const BREAKPOINTS = {
  lg: 992
};

const ReactivateBanner = () => {
  const activeVacation = useSelector(getActiveVacation);
  const user = useSelector(getUserData);
  const history = useHistory();
  const dispatch = useDispatch();
  const { isFeatureFlagEnabled } = useQueryFeatureFlag(FEATURE_FLAG_VACATION_MODE);
  const isManualSeller = user?.integrations?.some(integration => integration.name === "MR_GENERIC");

  const now = new Date();
  const startDate = activeVacation?.startDate ? parseISO(activeVacation.startDate) : null;
  const endDate = activeVacation?.endDate ? parseISO(activeVacation.endDate) : null;

  const isDuringVacation = startDate && endDate && isWithinInterval(now, { start: startDate, end: endDate });
  const isAfterVacation = endDate && isAfter(now, endDate);

  if ((!isDuringVacation && !isAfterVacation) || (isAfterVacation && !isManualSeller)) {
    return (
      <div
        style={useContainerValue({
          lg: { minHeight: 0 },
          default: { minHeight: MOBILE_NAV_HEIGHT }
        })}
      />
    );
  }

  const hubspotSrc = `${import.meta.env.VITE_HUBSPOT_URL}&firstname=${encodeURIComponent(
    user.first_name
  )}&lastname=${encodeURIComponent(user.last_name)}&email=${encodeURIComponent(
    user.email
  )}&company=${encodeURIComponent(user.retailer?.business_name)}`;

  const wrapperStyle = useContainerValue({
    lg: {
      padding: "15px",
      marginTop: 0,
      backgroundColor: "#FDE2CE",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      alignItems: "center"
    },
    default: {
      padding: "15px",
      backgroundColor: "#FDE2CE",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      alignItems: "center"
    }
  });

  const getBannerContent = () => {
    if (isDuringVacation) {
          return {
            text:
              "Your account is set to Vacation Mode. To reactivate your account, go to Settings, and Reactivate your account.",
            buttonText: "View My Settings",
            onClick: () => history.push("/settings/status")
          };
        }

    if (isAfterVacation) {
      return {
        text:
          "Your scheduled vacation is over – welcome back! To reactivate and allow new orders, confirm your inventory is up-to-date.",
        buttonText: "Reactivate my Account",
        onClick: () => {
          history.push("/settings/status");
          dispatch(setReactivateModalOpen());
        }
      };
    }

    return null;
  };

  const content = getBannerContent();

  if (!content) {
    return (
      <div
        style={useContainerValue({
          lg: { minHeight: 0 },
          default: { minHeight: MOBILE_NAV_HEIGHT }
        })}
      />
    );
  }

  const ReactivateButton = () => {
    if (!isFeatureFlagEnabled) {
      return (
        <ButtonLink
          as={CLink}
          href={hubspotSrc}
          isExternal
          fontSize="16px"
          borderRadius="4px"
          border="solid 1px #F78F45"
          marginLeft="auto"
          color="black"
          borderColor="#F78F45"
          backgroundColor="#FDE2CE"
          textTransform="none"
          _hover={{ backgroundColor: "#FDE2CE" }}
        >
          Schedule a call to activate
        </ButtonLink>
      );
    }

    return (
      <Button
        onClick={content.onClick}
        fontSize="16px"
        borderRadius="4px"
        border="solid 1px #F78F45"
        marginLeft="auto"
        color="black"
        borderColor="#F78F45"
        backgroundColor="#FDE2CE"
        textTransform="none"
        _hover={{ backgroundColor: "#FDE2CE" }}
      >
        {content.buttonText}
      </Button>
    );
  };

  return (
    <Flex style={wrapperStyle}>
      <YellowAlertInfo />
      &nbsp;&nbsp;
      <Text fontSize="16px" color="#1E2533" flex={1}>
        {content.text}
      </Text>
      <ReactivateButton />
    </Flex>
  );
};

export default withContainerBreakpoints(ReactivateBanner, BREAKPOINTS);
